import { useCallback } from 'react'
import type { BillingPlanV2Tier } from '@highlight/common/types'
import type { TopUpKind } from '@renderer/components/pricing/top-up-kinds'
import { useModals } from '../store/modals'
import { useUnifiedBilling } from '../store/global-billing'
import { useShowPaywall } from '@renderer/components/pricing/use-show-paywall'
import { isPlanEnded, topUpTier } from '../utils/billingUtils'

export interface CreditsTopUp {
  /** The tier a top-up would bill, or null when nothing here can charge the plan. */
  tier: BillingPlanV2Tier | null
  /** Opens a top-up for the given unit. A no-op when `tier` is null. */
  openTopUp: (kind: TopUpKind, from: string) => void
  /**
   * Sells credits, or an upgrade when the plan has none to buy. The paywall is
   * the wrong fallback inside the paywall itself, which branches on `tier`.
   */
  openCreditsPurchase: (from: string) => void
}

/**
 * The single way to sell a top-up. Buying draws down a v2 tier's balance, so a
 * plan naming no tier has nothing to buy: free accounts are sold an upgrade,
 * and legacy plans are shown nothing at all.
 */
export function useCreditsTopUp(): CreditsTopUp {
  const { openModal } = useModals()
  const showPaywall = useShowPaywall()
  const { paywall, subscription } = useUnifiedBilling()
  const plan = paywall.plan
  const tier = topUpTier({ plan, planEnded: isPlanEnded(subscription) })

  const openTopUp = useCallback(
    (kind: TopUpKind, from: string) => {
      if (tier === null) return
      openModal('top-up', { kind, from })
    },
    [tier, openModal],
  )

  const openCreditsPurchase = useCallback(
    (from: string) => {
      if (tier !== null) {
        openModal('top-up', { kind: 'credits', from })
        return
      }
      // useShowPaywall stands a legacy plan down; nothing here can bill it.
      showPaywall('credits')
    },
    [tier, openModal, showPaywall],
  )

  return { tier, openTopUp, openCreditsPurchase }
}
